import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER_NINJA } from '@/settings';
import type { Marketplace } from '@/store/organization/projectSlice';
import { useWindowSize } from '@uidotdev/usehooks';
import axios from 'axios';
import { useEffect, useRef, useState, type FC } from 'react';
import useSWR from 'swr';
import BarStackGoodChart from './bar-stack-good';

type IGoodProps = {
  product_id: string | number;
  end_date: string;
  start_date: string;
  keywords: string[];
  platform: Marketplace;
};

type IResponseItem = {
  date: string;
  price: number;
  rating: number;
  rating_count: number;
};

const Good: FC<IGoodProps> = ({
  product_id,
  end_date,
  start_date,
  keywords,
  platform,
}) => {
  const { data = [], isLoading } = useSWR(
    [
      BACKEND_SERVER_NINJA +
        '/reports/top_products/top_product/report_top_product',
      product_id,
    ],
    async ([url, product_id]) => {
      const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
      return await axios
        .post<{ data: IResponseItem[] }>(
          url,
          {
            product_id: String(product_id),
            end_date,
            start_date,
            keywords,
            platform,
          },
          { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
        )
        .then((res) => res?.data?.data?.flatMap((item) => item));
    }
  );

  const { width } = useWindowSize();
  const height = (width as number) > 650 ? 500 : 350;

  const chartRef = useRef<HTMLDivElement | null>(null);
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    const element = chartRef.current;
    let timeout: string | number | NodeJS.Timeout | undefined;
    if (element !== null) {
      const observer = new ResizeObserver((entries) => {
        clearTimeout(timeout);
        setShowChart(false);
        if (typeof entries[0]?.target?.clientWidth === 'number') {
          timeout = setTimeout(() => {
            setShowChart(true);
          }, 100);
        }
      });
      observer.observe(element);
      return () => observer.unobserve(element);
    }
    return;
  }, [chartRef]);

  return (
    <div ref={chartRef} className="w-full">
      <BarStackGoodChart
        isLoading={isLoading}
        data={data.map((dataItem) => ({
          date: dataItem.date,
          value: dataItem.price,
        }))}
        showChart={showChart}
        labels={{ x: 'Date', y: 'Price' }}
        height={height}
        title="График цены"
      />

      <div className="mt-2">
        <BarStackGoodChart
          isLoading={isLoading}
          data={data.map((dataItem) => ({
            date: dataItem.date,
            value: dataItem.rating,
          }))}
          showChart={showChart}
          labels={{ x: 'Date', y: 'Рейтинг' }}
          height={height}
          title="Динамика рейтинга"
        />
      </div>

      <div className="mt-2">
        <BarStackGoodChart
          isLoading={isLoading}
          data={data.map((dataItem) => ({
            date: dataItem.date,
            value: dataItem.rating_count,
          }))}
          showChart={showChart}
          labels={{ x: 'Date', y: 'Отзывы' }}
          height={height}
          title="Прирост отзывов"
        />
      </div>
    </div>
  );
};

export default Good;
